import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import config from '../config';
import { getOrganizationContext, setOrganizationContext } from '../orgContext';
import { getMembershipRoles, getUserRoles, hasAnyRole, mergeRoles } from '../navigationConfig';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminFilterBar, AdminPageHeader } from './components/AdminMasterDetail';
import { useAdminApi } from './hooks/useAdminApi';
import AdminAiAssistantModal from './components/AdminAiAssistantModal';

interface CategoryLookupItem {
  id: number;
  name: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

interface QuestionItem {
  id: number;
  categoryId: number;
  questionText: string;
  questionPrefix?: string | null;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson?: string | null;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex?: string | null;
  questionForOpenAI?: string | null;
  promptKey?: string | null;
  promptVersion?: string | null;
  promptModel?: string | null;
  promptResponseMode?: string | null;
  explanationGeneratedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface QuestionFormData {
  categoryId: number | null;
  questionText: string;
  questionPrefix: string;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson: string;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  optionE: string;
  correctOption: string;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex: string;
  questionForOpenAI: string;
  promptKey: string;
  promptVersion: string;
  promptModel: string;
  promptResponseMode: string;
}

interface UserOrganizationItem {
  organizationId: number;
  organizationSlug: string;
  organizationName?: string;
  isDefault?: boolean;
}

interface CourseLeafOption {
  id: number;
  name: string;
  pathLabel: string;
}

interface CategoryLeafOption {
  id: number;
  name: string;
  label?: string;
  pathLabel?: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const buildEmptyForm = (categoryId: number | null = null): QuestionFormData => ({
  categoryId,
  questionText: '',
  questionPrefix: '',
  answerText: '',
  explanation: '',
  answerExplanation: '',
  answerExplanationJson: '',
  hint: '',
  difficultyLevel: 1,
  isActive: true,
  source: '',
  imageUrl: '',
  optionA: '',
  optionB: '',
  optionC: '',
  optionD: '',
  optionE: '',
  correctOption: '',
  isMultipleChoice: true,
  questionLatex: '',
  answerLatex: '',
  graphLatex: '',
  questionForOpenAI: '',
  promptKey: '',
  promptVersion: '',
  promptModel: '',
  promptResponseMode: ''
});

const normalizeText = (value?: string | null): string => (value || '').trim();

const stripMarkdownCodeFence = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return trimmed
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
};

const containsMathNotation = (value?: string | null): boolean => {
  const text = normalizeText(value);
  if (!text) {
    return false;
  }

  return /\\[a-zA-Z]+|\^|_|\$|\b(?:sin|cos|tan|log|ln|sqrt|pi|theta|alpha|beta)\b|[\d\s][+\-*/=][\d\s]/i.test(text);
};

const prepareForMathJax = (value?: string | null): string => {
  const text = normalizeText(value);
  if (!text) return '';

  const hasDelimiters = /\$|\\\(|\\\[/.test(text);
  const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

  if (!hasDelimiters && looksLikeLatex) {
    return `\\(${text}\\)`;
  }

  return text;
};

const toHtmlMarkup = (value?: string | null): string => {
  const prepared = prepareForMathJax(value);
  if (!prepared) return '';

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
  if (hasHtmlTags) {
    return prepared;
  }

  return prepared
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br/>');
};

const isMultipleChoiceValid = (formData: QuestionFormData): boolean => {
  if (!formData.isMultipleChoice) {
    return true;
  }

  const options = [formData.optionA, formData.optionB, formData.optionC, formData.optionD, formData.optionE]
    .map((option) => option.trim())
    .filter((option) => option.length > 0);

  return options.length >= 2 && ['A', 'B', 'C', 'D', 'E'].includes(formData.correctOption.trim().toUpperCase());
};

const ManageQuestions: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [organizationContext, setOrganizationContextState] = useState(() => getOrganizationContext());
  const [organizations, setOrganizations] = useState<UserOrganizationItem[]>([]);
  const [membershipRoles, setMembershipRoles] = useState<string[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseLeafOption[]>([]);
  const [courseCategoryOptions, setCourseCategoryOptions] = useState<CategoryLeafOption[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);

  const [categories, setCategories] = useState<CategoryLookupItem[]>([]);
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [difficultyFilter, setDifficultyFilter] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [searchText, setSearchText] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingLeafCategories, setLoadingLeafCategories] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>(buildEmptyForm());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [bulkAiCount, setBulkAiCount] = useState(10);
  const [bulkAiDrafts, setBulkAiDrafts] = useState<any[]>([]);
  const [bulkAiReviewOpen, setBulkAiReviewOpen] = useState(false);
  const [bulkAiSaving, setBulkAiSaving] = useState(false);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => {
      const firstOrder = a.sortOrder && a.sortOrder > 0 ? a.sortOrder : 1;
      const secondOrder = b.sortOrder && b.sortOrder > 0 ? b.sortOrder : 1;
      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }
      return a.name.localeCompare(b.name);
    }),
    [categories]
  );

  const categoryNameMap = useMemo(() => {
    const map: Record<number, string> = {};

    categories.forEach((category) => {
      map[category.id] = category.name;
    });

    courseCategoryOptions.forEach((category) => {
      map[category.id] = category.pathLabel || category.label || category.name;
    });

    return map;
  }, [categories, courseCategoryOptions]);

  const currentUserRoles = useMemo(() => mergeRoles(getUserRoles(user), membershipRoles), [membershipRoles, user]);
  const isAdminLikeAccess = useMemo(
    () => hasAnyRole(currentUserRoles, ['superadmin', 'owner', 'principal', 'staff', 'admin']),
    [currentUserRoles]
  );

  const selectedCourse = useMemo(
    () => courseOptions.find((course) => course.id === selectedCourseId) || null,
    [courseOptions, selectedCourseId]
  );

  const leafCategoriesForSelectedCourse = useMemo(() => {
    return [...courseCategoryOptions].sort((first, second) => {
      const firstLabel = (first.pathLabel || first.label || first.name || '').toLowerCase();
      const secondLabel = (second.pathLabel || second.label || second.name || '').toLowerCase();
      return firstLabel.localeCompare(secondLabel);
    });
  }, [courseCategoryOptions]);

  const filteredQuestions = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return questions.filter((question) => {
      if (search) {
        const haystack = [
          question.questionText,
          question.questionPrefix,
          question.answerText,
          question.hint,
          question.source,
          question.correctOption
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (difficultyFilter && question.difficultyLevel !== difficultyFilter) {
        return false;
      }

      return true;
    });
  }, [difficultyFilter, questions, searchText]);

  const selectedCategory = useMemo(
    () => sortedCategories.find((category) => category.id === categoryFilter) || null,
    [categoryFilter, sortedCategories]
  );

  const getAuthToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const loadOrganizations = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get<UserOrganizationItem[]>(`${API_BASE_URL}/organizations/my`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const list = Array.isArray(response.data) ? response.data : [];
      setOrganizations(list);

      if (!organizationContext && list.length > 0) {
        const fallback = list.find((org) => org.isDefault) ?? list[0];
        const nextContext = {
          organizationId: fallback.organizationId,
          organizationSlug: fallback.organizationSlug,
          organizationName: fallback.organizationName
        };
        setOrganizationContext(nextContext);
        setOrganizationContextState(nextContext);
      }
    } catch (loadError) {
      console.error('Failed to load organizations:', loadError);
    }
  }, [getAuthToken, organizationContext]);

  const loadQuestions = useCallback(async (categoryId: number | null, difficulty: number | null, activeState: 'all' | 'active' | 'inactive') => {
    if (!categoryId) {
      setQuestions([]);
      return;
    }

    try {
      setLoadingQuestions(true);
      const params = new URLSearchParams();
      params.set('categoryId', String(categoryId));
      if (difficulty) {
        params.set('difficultyLevel', String(difficulty));
      }
      if (activeState === 'active') {
        params.set('isActive', 'true');
      } else if (activeState === 'inactive') {
        params.set('isActive', 'false');
      }

      if (!hasActiveOrganization) {
        setQuestions([]);
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<QuestionItem[]>(`${API_BASE_URL}/questions?${params.toString()}`, {
        headers: getOrganizationHeaders(token)
      });
      setQuestions(response.data || []);
    } catch (loadError) {
      console.error('Failed to load questions:', loadError);
      setError('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const loadAccessibleCourses = useCallback(async () => {
    if (!hasActiveOrganization) {
      setCourseOptions([]);
      setCourseCategoryOptions([]);
      setSelectedCourseId(null);
      setCategoryFilter(null);
      return;
    }

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const response = await axios.get<CourseLeafOption[]>(`${API_BASE_URL}/courses/leaf-options`, { headers });
      const allowedCourses = Array.isArray(response.data) ? response.data : [];

      setCourseOptions(allowedCourses);
      setSelectedCourseId((currentCourseId) => {
        if (currentCourseId && allowedCourses.some((course) => course.id === currentCourseId)) {
          return currentCourseId;
        }

        return allowedCourses[0]?.id ?? null;
      });
    } catch (loadError) {
      console.error('Failed to load accessible courses:', loadError);
      setCourseOptions([]);
      setCourseCategoryOptions([]);
      setSelectedCourseId(null);
      setCategoryFilter(null);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      if (!hasActiveOrganization) {
        setCategories([]);
        setCategoryFilter(null);
        setFormData((previous) => ({ ...previous, categoryId: null }));
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<CategoryLookupItem[]>(`${API_BASE_URL}/categories/lookup`, {
        headers: getOrganizationHeaders(token)
      });
      const loadedCategories = Array.isArray(response.data) ? response.data : [];
      setCategories(loadedCategories);
      setFormData((previous) => {
        if (previous.categoryId !== null) {
          return previous;
        }
        return { ...previous, categoryId: null };
      });
    } catch (loadError) {
      console.error('Failed to load categories:', loadError);
      setError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMembershipRoles([]);
      return;
    }

    let cancelled = false;

    const loadMembershipRoles = async () => {
      try {
        const token = await getAuthToken();
        const response = await axios.get<UserOrganizationItem[]>(`${API_BASE_URL}/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!cancelled && Array.isArray(response.data)) {
          setMembershipRoles(getMembershipRoles(response.data));
        }
      } catch (error) {
        console.warn('Unable to load organization membership roles for manage questions', error);
      }
    };

    void loadMembershipRoles();

    return () => {
      cancelled = true;
    };
  }, [getAuthToken, isAuthenticated]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadCategories();
    loadAccessibleCourses();
  }, [loadCategories, loadAccessibleCourses]);

  useEffect(() => {
    if (!selectedCourseId) {
      setCourseCategoryOptions([]);
      setCategoryFilter(null);
      return;
    }

    const loadLeafCategoriesForCourse = async () => {
      setLoadingLeafCategories(true);
      setCategoryFilter(null);
      setQuestions([]);

      try {
        const token = await getManagementToken();
        const response = await axios.get<CategoryLeafOption[]>(`${API_BASE_URL}/categories/lookup/leaf?courseId=${selectedCourseId}`, {
          headers: getOrganizationHeaders(token)
        });

        const nextCategories = Array.isArray(response.data) ? response.data : [];
        setCourseCategoryOptions(nextCategories);

        const nextCategoryId = nextCategories[0]?.id ?? null;
        setCategoryFilter((previousCategoryId) => {
          if (!previousCategoryId) {
            return nextCategoryId;
          }

          const isValid = nextCategories.some((category) => category.id === previousCategoryId);
          return isValid ? previousCategoryId : nextCategoryId;
        });
      } catch (loadError) {
        console.error('Failed to load leaf categories for selected course:', loadError);
        setCourseCategoryOptions([]);
        setCategoryFilter(null);
      } finally {
        setLoadingLeafCategories(false);
      }
    };

    void loadLeafCategoriesForCourse();
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, selectedCourseId]);

  useEffect(() => {
    if (!categoryFilter) {
      setQuestions([]);
      return;
    }

    loadQuestions(categoryFilter, difficultyFilter, activeFilter);
  }, [activeFilter, categoryFilter, difficultyFilter, loadQuestions]);

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }

    if (formData.categoryId === null || !sortedCategories.some((category) => category.id === formData.categoryId)) {
      setFormData((previous) => ({ ...previous, categoryId: selectedCategory.id }));
    }
  }, [formData.categoryId, selectedCategory, sortedCategories]);

  const resetForm = useCallback((categoryId: number | null = categoryFilter) => {
    setEditingQuestionId(null);
    setShowForm(false);
    setFormData(buildEmptyForm(categoryId ?? leafCategoriesForSelectedCourse[0]?.id ?? categories[0]?.id ?? null));
  }, [categories, categoryFilter, leafCategoriesForSelectedCourse]);

  const setBooleanField = useCallback((field: 'isActive' | 'isMultipleChoice', checked: boolean) => {
    setFormData((previous) => {
      if (previous[field] === checked) {
        return previous;
      }

      return { ...previous, [field]: checked };
    });
  }, []);

  const beginCreate = useCallback(() => {
    if (!selectedCourseId && courseOptions.length > 0) {
      setSelectedCourseId(courseOptions[0].id);
    }

    const defaultCategoryId = categoryFilter ?? leafCategoriesForSelectedCourse[0]?.id ?? categories[0]?.id ?? null;
    setEditingQuestionId(null);
    setShowForm(true);
    setError(null);
    setMessage(null);
    setFormData(buildEmptyForm(defaultCategoryId));
  }, [categories, categoryFilter, courseOptions, leafCategoriesForSelectedCourse, selectedCourseId]);

  const selectCategory = (categoryId: number | null) => {
    setCategoryFilter(categoryId);
    setEditingQuestionId(null);
    setMessage(null);
    setError(null);
    setFormData((previous) => ({
      ...previous,
      categoryId: categoryId ?? previous.categoryId
    }));
  };

  const handleOrganizationChange = async (organizationId: number) => {
    const organization = organizations.find((item) => item.organizationId === organizationId);
    if (!organization) {
      return;
    }

    const nextContext = {
      organizationId: organization.organizationId,
      organizationSlug: organization.organizationSlug,
      organizationName: organization.organizationName
    };

    setOrganizationContext(nextContext);
    setOrganizationContextState(nextContext);
    setCategoryFilter(null);
    setQuestions([]);
    resetForm(null);
    await loadCategories();
  };

  const handleEdit = (question: QuestionItem) => {
    setEditingQuestionId(question.id);
    setShowForm(true);
    setMessage(null);
    setError(null);
    setCategoryFilter(question.categoryId);
    setFormData({
      categoryId: question.categoryId,
      questionText: question.questionText || '',
      questionPrefix: question.questionPrefix || '',
      answerText: question.answerText || '',
      explanation: question.explanation || '',
      answerExplanation: question.answerExplanation || '',
      answerExplanationJson: question.answerExplanationJson || '',
      hint: question.hint || '',
      difficultyLevel: question.difficultyLevel || 1,
      isActive: question.isActive,
      source: question.source || '',
      imageUrl: question.imageUrl || '',
      optionA: question.optionA || '',
      optionB: question.optionB || '',
      optionC: question.optionC || '',
      optionD: question.optionD || '',
      optionE: question.optionE || '',
      correctOption: question.correctOption || '',
      isMultipleChoice: question.isMultipleChoice,
      questionLatex: question.questionLatex || '',
      answerLatex: question.answerLatex || '',
      graphLatex: question.graphLatex || '',
      questionForOpenAI: question.questionForOpenAI || '',
      promptKey: question.promptKey || '',
      promptVersion: question.promptVersion || '',
      promptModel: question.promptModel || '',
      promptResponseMode: question.promptResponseMode || ''
    });
  };

  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsUploadingImage(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('assetType', 'question');
      uploadFormData.append('entityType', 'question');

      const response = await fetch(`${API_BASE_URL}/assets/upload`, {
        method: 'POST',
        headers: getOrganizationHeaders(token),
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to upload image');
      }

      const data = await response.json();
      setFormData((previous) => ({ ...previous, imageUrl: data.url || '' }));
      setMessage('Image uploaded successfully.');
    } catch (uploadError: any) {
      console.error('Failed to upload image:', uploadError);
      setError(uploadError?.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      event.target.value = '';
    }
  }, [getManagementToken, getOrganizationHeaders]);

  const handleDelete = async (questionId: number) => {
    if (!window.confirm('Delete this question?')) {
      return;
    }

    try {
      setSaving(true);
      const token = await getManagementToken();
      await axios.delete(`${API_BASE_URL}/questions/${questionId}`, {
        headers: getOrganizationHeaders(token)
      });

      if (editingQuestionId === questionId) {
        resetForm(categoryFilter);
      }

      if (categoryFilter) {
        await loadQuestions(categoryFilter, difficultyFilter, activeFilter);
      }
      setMessage('Question deleted successfully.');
    } catch (deleteError: any) {
      console.error('Failed to delete question:', deleteError);
      setError(deleteError.response?.data?.message || 'Failed to delete question');
    } finally {
      setSaving(false);
    }
  };

  const parseBulkQuestionDrafts = useCallback((generatedText: string): any[] => {
    const sanitized = stripMarkdownCodeFence(generatedText);
    if (!sanitized) {
      return [];
    }

    const parsed = JSON.parse(sanitized);
    const values = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.questions) ? parsed.questions : []);
    return values.filter((item: any) => item && typeof item === 'object');
  }, []);

  const normalizeBulkQuestionDraft = useCallback((draft: any, index: number, categoryId: number) => {
    const questionText = normalizeText(draft?.questionText ?? draft?.QuestionText ?? draft?.question ?? draft?.promptText);
    const answerText = normalizeText(draft?.answerText ?? draft?.AnswerText ?? draft?.answer ?? draft?.correctAnswer);
    const explanation = normalizeText(draft?.explanation ?? draft?.Explanation ?? '');
    const answerExplanation = normalizeText(draft?.answerExplanation ?? draft?.AnswerExplanation ?? explanation);
    const hint = normalizeText(draft?.hint ?? draft?.Hint ?? '');
    const difficultyLevel = Number(draft?.difficultyLevel ?? draft?.DifficultyLevel ?? 1);
    const source = normalizeText(draft?.source ?? draft?.Source ?? 'AI Generated');
    const optionA = normalizeText(draft?.optionA ?? draft?.OptionA ?? '');
    const optionB = normalizeText(draft?.optionB ?? draft?.OptionB ?? '');
    const optionC = normalizeText(draft?.optionC ?? draft?.OptionC ?? '');
    const optionD = normalizeText(draft?.optionD ?? draft?.OptionD ?? '');
    const optionE = normalizeText(draft?.optionE ?? draft?.OptionE ?? '');
    const correctOption = normalizeText(draft?.correctOption ?? draft?.CorrectOption ?? '').toUpperCase();
    const selectedOptions: Record<string, string> = { A: optionA, B: optionB, C: optionC, D: optionD, E: optionE };
    const resolvedCorrectOption = ['A', 'B', 'C', 'D', 'E'].includes(correctOption) && selectedOptions[correctOption]
      ? correctOption
      : (Object.entries(selectedOptions).find(([, optionValue]) => optionValue && optionValue === answerText)?.[0] ?? 'A');

    const questionLatex = containsMathNotation(questionText) ? (draft?.questionLatex ?? draft?.QuestionLatex ?? questionText) : '';
    const answerLatex = containsMathNotation(answerText) ? (draft?.answerLatex ?? draft?.AnswerLatex ?? answerText) : '';

    return {
      categoryId,
      questionText: questionText || `Generated question ${index + 1}`,
      questionPrefix: normalizeText(draft?.questionPrefix ?? draft?.QuestionPrefix ?? ''),
      answerText: answerText || selectedOptions[resolvedCorrectOption] || 'Answer unavailable',
      explanation: explanation || 'Review the options and identify the correct answer.',
      answerExplanation: answerExplanation || explanation || 'Review the options and identify the correct answer.',
      answerExplanationJson: normalizeText(draft?.answerExplanationJson ?? draft?.AnswerExplanationJson ?? ''),
      hint: hint || 'Use the information provided carefully.',
      difficultyLevel: Number.isFinite(difficultyLevel) ? Math.min(3, Math.max(1, difficultyLevel)) : 1,
      isActive: false,
      source: source || 'AI Generated',
      imageUrl: normalizeText(draft?.imageUrl ?? draft?.ImageUrl ?? ''),
      optionA,
      optionB,
      optionC,
      optionD,
      optionE,
      correctOption: resolvedCorrectOption,
      isMultipleChoice: true,
      questionLatex,
      answerLatex,
      graphLatex: normalizeText(draft?.graphLatex ?? draft?.GraphLatex ?? ''),
      questionForOpenAI: normalizeText(draft?.questionForOpenAI ?? draft?.QuestionForOpenAI ?? questionText),
      promptKey: 'admin-questions-bulk-ai',
      promptVersion: 'v1',
      promptModel: 'gpt-4.1',
      promptResponseMode: 'json',
      approvalStatus: 'Pending Review'
    };
  }, []);

  const validateBulkQuestionDrafts = useCallback((drafts: any[]) => {
    const errors: string[] = [];
    const validDrafts = drafts
      .map((draft, index) => {
        const normalized = normalizeBulkQuestionDraft(draft, index, categoryFilter ?? 0);

        const optionValues = ['A', 'B', 'C', 'D', 'E']
          .map((letter) => ({ letter, value: normalized[`option${letter}` as keyof typeof normalized] as string }))
          .filter((entry) => entry.value && entry.value.trim().length > 0);

        if (!normalized.questionText || optionValues.length < 2) {
          errors.push(`Question ${index + 1} is missing required question text or enough options.`);
          return null;
        }

        if (!['A', 'B', 'C', 'D', 'E'].includes(normalized.correctOption)) {
          errors.push(`Question ${index + 1} has an invalid CorrectOption.`);
          return null;
        }

        if (!optionValues.some((entry) => entry.letter === normalized.correctOption)) {
          errors.push(`Question ${index + 1} does not match a valid correct option.`);
          return null;
        }

        return normalized;
      })
      .filter((draft): draft is any => Boolean(draft));

    return { validDrafts, errors };
  }, [categoryFilter, normalizeBulkQuestionDraft]);

  const handleBulkAiApply = useCallback((generatedText: string) => {
    try {
      const parsed = parseBulkQuestionDrafts(generatedText);
      if (!parsed.length) {
        setError('The AI response did not contain a valid question array.');
        return;
      }

      const targetCategoryId = categoryFilter ?? leafCategoriesForSelectedCourse[0]?.id ?? null;
      if (!targetCategoryId) {
        setError('Please select a category before generating questions.');
        return;
      }

      const { validDrafts, errors } = validateBulkQuestionDrafts(parsed);
      if (errors.length > 0) {
        setError(`Some generated questions were invalid: ${errors.slice(0, 3).join(' ')}`);
        return;
      }

      const canonicalDrafts = validDrafts
        .map((draft, index) => ({
          ...draft,
          categoryId: targetCategoryId,
          questionText: draft.questionText,
          answerText: draft.answerText,
          promptKey: 'admin-questions-bulk-ai',
          promptVersion: 'v1',
          promptModel: 'gpt-4.1',
          promptResponseMode: 'json',
          approvalStatus: 'Pending Review',
          isActive: false,
          source: 'AI Generated',
          questionForOpenAI: draft.questionForOpenAI || draft.questionText,
          index: index + 1,
        }));

      const uniqueByStem = new Map<string, any>();
      canonicalDrafts.forEach((draft) => {
        const key = draft.questionText.trim().toLowerCase();
        if (!uniqueByStem.has(key)) {
          uniqueByStem.set(key, draft);
        }
      });

      const dedupedDrafts = Array.from(uniqueByStem.values()).slice(0, Math.max(1, bulkAiCount));
      if (dedupedDrafts.length < 1) {
        setError('No valid AI question drafts were produced.');
        return;
      }

      setBulkAiDrafts(dedupedDrafts);
      setBulkAiReviewOpen(true);
      setMessage('AI generated question drafts are ready for review.');
      setError(null);
    } catch (error: any) {
      console.error('Failed to parse bulk AI question output:', error);
      setError('The AI response could not be parsed into valid questions. Please try again with a simpler prompt.');
    }
  }, [bulkAiCount, categoryFilter, leafCategoriesForSelectedCourse, parseBulkQuestionDrafts, validateBulkQuestionDrafts]);

  const saveBulkGeneratedQuestions = useCallback(async () => {
    if (!bulkAiDrafts.length) {
      return;
    }

    try {
      setBulkAiSaving(true);
      setError(null);
      const token = await getManagementToken();
      const requestBody = bulkAiDrafts.map((draft) => ({
        categoryId: draft.categoryId,
        approvalStatus: draft.approvalStatus || 'Pending Review',
        questionText: draft.questionText,
        questionPrefix: draft.questionPrefix || '',
        answerText: draft.answerText,
        explanation: draft.explanation,
        answerExplanation: draft.answerExplanation,
        answerExplanationJson: draft.answerExplanationJson || '',
        hint: draft.hint,
        difficultyLevel: draft.difficultyLevel,
        source: draft.source || 'AI Generated',
        imageUrl: draft.imageUrl || '',
        optionA: draft.optionA,
        optionB: draft.optionB,
        optionC: draft.optionC,
        optionD: draft.optionD,
        optionE: draft.optionE,
        correctOption: draft.correctOption,
        isMultipleChoice: draft.isMultipleChoice,
        questionLatex: draft.questionLatex || '',
        answerLatex: draft.answerLatex || '',
        graphLatex: draft.graphLatex || '',
        questionForOpenAI: draft.questionForOpenAI || draft.questionText,
        promptKey: draft.promptKey || 'admin-questions-bulk-ai',
        promptVersion: draft.promptVersion || 'v1',
        promptModel: draft.promptModel || 'gpt-4.1',
        promptResponseMode: draft.promptResponseMode || 'json',
        explanationGeneratedAt: null,
      }));

      await axios.post(`${API_BASE_URL}/questions/bulk`, requestBody, {
        headers: getOrganizationHeaders(token),
      });

      setBulkAiReviewOpen(false);
      setBulkAiDrafts([]);
      setMessage(`Created ${requestBody.length} AI-generated questions.`);
      if (categoryFilter) {
        await loadQuestions(categoryFilter, difficultyFilter, activeFilter);
      }
    } catch (saveError: any) {
      console.error('Failed to save AI-generated question batch:', saveError);
      setError(saveError.response?.data?.message || 'Failed to save AI-generated questions');
    } finally {
      setBulkAiSaving(false);
    }
  }, [bulkAiDrafts, categoryFilter, difficultyFilter, activeFilter, getManagementToken, getOrganizationHeaders, loadQuestions]);

  const handleBulkAiOpen = useCallback(() => {
    if (!selectedCourseId) {
      setError('Please select a course before generating questions.');
      return;
    }

    if (!categoryFilter) {
      setError('Please select a category before generating questions.');
      return;
    }

    setAiModalOpen(true);
  }, [categoryFilter, selectedCourseId]);

  const buildBulkQuestionPrompt = useCallback(() => {
    const courseName = selectedCourse?.name ?? 'the selected course';
    const categoryName = selectedCategory?.name ?? 'the selected category';
    return `Generate exactly ${bulkAiCount} unique multiple-choice questions for course "${courseName}" and category "${categoryName}". Ensure every question is non-repetitive, varied in wording and context, and aligned to the category. Include 5 options A-E, one valid CorrectOption, a clear answer text, explanation, hint, and difficulty level. Only populate questionLatex and answerLatex when the question or answer uses mathematical notation or formulas; otherwise leave them empty or null. Return valid JSON only as an array of question objects.`;
  }, [bulkAiCount, selectedCategory, selectedCourse]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const categoryId = formData.categoryId;
      if (!categoryId) {
        setError('Category is required');
        return;
      }

      if (!normalizeText(formData.questionText)) {
        setError('Question text is required');
        return;
      }

      if (formData.difficultyLevel < 1 || formData.difficultyLevel > 3) {
        setError('Difficulty must be 1, 2, or 3');
        return;
      }

      if (!isMultipleChoiceValid(formData)) {
        setError('Multiple-choice questions require at least two options and a valid correct option (A-E).');
        return;
      }

      const payload = {
        categoryId,
        questionText: formData.questionText,
        questionPrefix: formData.questionPrefix,
        answerText: formData.answerText,
        explanation: formData.explanation,
        answerExplanation: formData.answerExplanation,
        answerExplanationJson: formData.answerExplanationJson,
        hint: formData.hint,
        difficultyLevel: formData.difficultyLevel,
        isActive: formData.isActive,
        source: formData.source,
        imageUrl: formData.imageUrl,
        optionA: formData.optionA,
        optionB: formData.optionB,
        optionC: formData.optionC,
        optionD: formData.optionD,
        optionE: formData.optionE,
        correctOption: formData.correctOption,
        isMultipleChoice: formData.isMultipleChoice,
        questionLatex: formData.questionLatex,
        answerLatex: formData.answerLatex,
        graphLatex: formData.graphLatex,
        questionForOpenAI: formData.questionForOpenAI,
        promptKey: formData.promptKey,
        promptVersion: formData.promptVersion,
        promptModel: formData.promptModel,
        promptResponseMode: formData.promptResponseMode
      };

      const token = await getManagementToken();
      if (editingQuestionId) {
        await axios.put(`${API_BASE_URL}/questions/${editingQuestionId}`, payload, {
          headers: getOrganizationHeaders(token)
        });
        setMessage('Question updated successfully.');
      } else {
        await axios.post(`${API_BASE_URL}/questions`, payload, {
          headers: getOrganizationHeaders(token)
        });
        setMessage('Question created successfully.');
      }

      const nextCategoryId = categoryId;
      setCategoryFilter(nextCategoryId);
      await loadQuestions(nextCategoryId, difficultyFilter, activeFilter);
      resetForm(nextCategoryId);
    } catch (saveError: any) {
      console.error('Failed to save question:', saveError);
      setError(saveError.response?.data?.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const previewList = filteredQuestions;
  const previewQuestionMarkup = useMemo(() => toHtmlMarkup(formData.questionText || 'Question preview will appear here.'), [formData.questionText]);

  const filterConfigs = useMemo(() => [
    {
      key: 'course',
      label: 'Course',
      value: selectedCourseId ? String(selectedCourseId) : '',
      onChange: (value: string) => setSelectedCourseId(value ? Number(value) : null),
      disabled: loadingLeafCategories,
      options: [
        { value: '', label: 'Select a course' },
        ...courseOptions.map((course) => ({ value: String(course.id), label: course.pathLabel || course.name })),
      ],
    },
    {
      key: 'category',
      label: 'Category',
      value: categoryFilter ? String(categoryFilter) : '',
      onChange: (value: string) => selectCategory(value ? Number(value) : null),
      disabled: loadingLeafCategories || !selectedCourseId,
      options: [
        { value: '', label: 'Select a category' },
        ...leafCategoriesForSelectedCourse.map((category) => ({ value: String(category.id), label: category.pathLabel || category.label || category.name })),
      ],
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      value: difficultyFilter ? String(difficultyFilter) : '',
      onChange: (value: string) => setDifficultyFilter(value ? Number(value) : null),
      disabled: loadingLeafCategories,
      options: [
        { value: '', label: 'All Difficulties' },
        { value: '1', label: 'Easy' },
        { value: '2', label: 'Medium' },
        { value: '3', label: 'Hard' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      value: activeFilter,
      onChange: (value: string) => setActiveFilter(value as 'all' | 'active' | 'inactive'),
      disabled: loadingLeafCategories,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'all', label: 'All Status' },
      ],
    },
  ], [activeFilter, categoryFilter, courseOptions, difficultyFilter, leafCategoriesForSelectedCourse, loadingLeafCategories, selectCategory, selectedCourseId]);

  if (loadingCategories) {
    return <div style={{ padding: 24, fontSize: 18 }}>Loading questions...</div>;
  }

  return (
    <MathJaxContext>
      <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
        <AdminBreadcrumbs
          items={[
            { label: 'Administration', to: '/analytics' },
            { label: 'Manage Questions', isCurrent: true },
          ]}
        />
        <AdminPageHeader
          title="Manage Questions"
          subtitle="Create, edit, delete, and preview questions by category."
          actions={[
            {
              key: 'new-question',
              label: 'New Question',
              variant: 'primary',
              onClick: () => beginCreate(),
            },
            {
              key: 'bulk-ai-generate-questions',
              label: 'Bulk AI Generate Questions',
              variant: 'secondary',
              onClick: handleBulkAiOpen,
            },
          ]}
        />

        {error && (
          <div style={{ marginBottom: 16, backgroundColor: '#ffebee', color: '#c62828', padding: 12, borderRadius: 6 }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}
            >
              ×
            </button>
          </div>
        )}

        {message && (
          <div style={{ marginBottom: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 6 }}>
            {message}
            <button
              onClick={() => setMessage(null)}
              style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', color: '#2e7d32' }}
            >
              ×
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gap: 16, alignItems: 'start' }}>
          <div style={{ border: '1px solid #dfe4ee', borderRadius: 10, backgroundColor: '#fff', padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#222' }}>Question List</h3>

            <div style={{ marginBottom: 12 }}>
              <AdminFilterBar
                searchValue={searchText}
                onSearchChange={setSearchText}
                searchPlaceholder="Search question text, source, answer..."
                filters={filterConfigs}
                isBusy={loadingLeafCategories}
              />
            </div>

            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              {selectedCourse ? `Course: ${selectedCourse.name}` : 'Choose a course to load questions.'}
              {selectedCategory ? ` • Category: ${selectedCategory.name}` : ''}
            </div>

            <div style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 280, overflowY: 'auto', display: 'grid', gap: 10 }}>
              {!selectedCourseId ? (
                <div style={{ color: '#777' }}>No accessible course is available for your account.</div>
              ) : !categoryFilter ? (
                <div style={{ color: '#777' }}>Select a leaf category to load questions.</div>
              ) : loadingQuestions ? (
                <div style={{ color: '#777' }}>Loading questions...</div>
              ) : previewList.length === 0 ? (
                <div style={{ color: '#777' }}>No questions found for the selected filters.</div>
              ) : (
                previewList.map((question) => {
                  const isEditing = editingQuestionId === question.id;
                  const options = [
                    { label: 'A', value: question.optionA },
                    { label: 'B', value: question.optionB },
                    { label: 'C', value: question.optionC },
                    { label: 'D', value: question.optionD },
                    { label: 'E', value: question.optionE }
                  ].filter((option) => !!option.value && option.value.trim().length > 0);

                  return (
                    <div
                      key={question.id}
                      style={{
                        border: isEditing ? '1px solid #90caf9' : '1px solid #e0e0e0',
                        backgroundColor: isEditing ? '#e3f2fd' : '#fff',
                        borderRadius: 8,
                        padding: 12
                      }}
                    >
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                        Q{question.id} • {categoryNameMap[question.categoryId] || `Category ${question.categoryId}`} • Difficulty {question.difficultyLevel} • {question.isActive ? 'Active' : 'Inactive'}
                      </div>
                      <div style={{ color: '#333', marginBottom: 8, lineHeight: 1.4, textAlign: 'left', fontWeight: 700 }}>
                        <MathJax dynamic>
                          <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(question.questionText) }} />
                        </MathJax>
                        {question.imageUrl && (
                          <div style={{ marginTop: 8 }}>
                            <img
                              src={question.imageUrl}
                              alt="Question illustration"
                              style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid #e0e0e0' }}
                            />
                          </div>
                        )}
                      </div>

                      {options.length > 0 && (
                        <div style={{ marginBottom: 8, fontSize: 12, color: '#444', display: 'grid', gap: 3 }}>
                          {options.map((option) => (
                            <div key={`${question.id}-${option.label}`} style={{ textAlign: 'left' }}>
                              <strong>{option.label}.</strong>{' '}
                              <span style={{ display: 'inline-block' }}>
                                <MathJax inline dynamic>
                                  <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.value) }} />
                                </MathJax>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleEdit(question)}
                          className="amd-btn amd-btn-primary"
                          style={{ fontSize: 12, padding: '6px 12px' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(question.id)}
                          disabled={saving}
                          className="amd-btn amd-btn-secondary"
                          style={{ fontSize: 12, padding: '6px 12px', borderColor: '#f9c2c2', color: '#b42318' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
          <div style={{ width: 'min(1100px, 92vw)', maxHeight: '90vh', overflowY: 'auto', backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 20px 64px rgba(15, 23, 42, 0.18)', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#222' }}>{editingQuestionId ? `Edit Question #${editingQuestionId}` : 'Create Question'}</h3>
              <button type="button" onClick={() => resetForm(categoryFilter)} style={{ background: 'transparent', border: '1px solid #cdd5df', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}>Close</button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Course</label>
                  <select
                    value={selectedCourseId ?? ''}
                    onChange={(event) => setSelectedCourseId(event.target.value ? Number(event.target.value) : null)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select course</option>
                    {courseOptions.map((course) => (
                      <option key={course.id} value={course.id}>{course.pathLabel || course.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Category *</label>
                  <select
                    value={formData.categoryId ?? ''}
                    onChange={(event) => setFormData((previous) => ({ ...previous, categoryId: event.target.value ? Number(event.target.value) : null }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select category</option>
                    {leafCategoriesForSelectedCourse.map((category) => (
                      <option key={category.id} value={category.id}>{category.pathLabel || category.label || category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Difficulty *</label>
                  <select
                    value={formData.difficultyLevel}
                    onChange={(event) => setFormData((previous) => ({ ...previous, difficultyLevel: Number(event.target.value) }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="1">Easy</option>
                    <option value="2">Medium</option>
                    <option value="3">Hard</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Type</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', minHeight: 42 }}>
                    <input
                      type="checkbox"
                      checked={formData.isMultipleChoice}
                      onChange={(event) => setBooleanField('isMultipleChoice', event.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>Multiple choice</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Status</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', minHeight: 42 }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(event) => setBooleanField('isActive', event.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>Active</span>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Text *</label>
                <textarea
                  value={formData.questionText}
                  onChange={(event) => setFormData((previous) => ({ ...previous, questionText: event.target.value }))}
                  rows={6}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Prefix</label>
                  <input
                    type="text"
                    value={formData.questionPrefix}
                    onChange={(event) => setFormData((previous) => ({ ...previous, questionPrefix: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Source</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(event) => setFormData((previous) => ({ ...previous, source: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Text</label>
                  <input
                    type="text"
                    value={formData.answerText}
                    onChange={(event) => setFormData((previous) => ({ ...previous, answerText: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Hint</label>
                  <input
                    type="text"
                    value={formData.hint}
                    onChange={(event) => setFormData((previous) => ({ ...previous, hint: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Explanation</label>
                <textarea
                  value={formData.explanation}
                  onChange={(event) => setFormData((previous) => ({ ...previous, explanation: event.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Explanation</label>
                <textarea
                  value={formData.answerExplanation}
                  onChange={(event) => setFormData((previous) => ({ ...previous, answerExplanation: event.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              {formData.isMultipleChoice && (
                <div style={{ border: '1px solid #eceff4', borderRadius: 8, padding: 12, backgroundColor: '#fafafa' }}>
                  <h4 style={{ marginTop: 0, marginBottom: 10 }}>Multiple Choice Options</h4>
                  <div style={{ display: 'grid', gap: 10 }}>
                    {(['optionA', 'optionB', 'optionC', 'optionD', 'optionE'] as const).map((field, index) => (
                      <div key={field}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>{String.fromCharCode(65 + index)}</label>
                        <input
                          type="text"
                          value={formData[field]}
                          onChange={(event) => setFormData((previous) => ({ ...previous, [field]: event.target.value }))}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Correct Option</label>
                    <select
                      value={formData.correctOption}
                      onChange={(event) => setFormData((previous) => ({ ...previous, correctOption: event.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                    >
                      <option value="">Select correct option</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setAiModalOpen(true)}>AI Assist</button>
                <button onClick={handleSave} disabled={saving} style={{ backgroundColor: saving ? '#90caf9' : '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  {saving ? 'Saving...' : editingQuestionId ? 'Update Question' : 'Create Question'}
                </button>
                <button onClick={() => resetForm(categoryFilter)} style={{ backgroundColor: '#757575', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}>
                  Reset
                </button>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <h4 style={{ marginTop: 0 }}>Preview</h4>
                <div style={{ color: '#333', lineHeight: 1.4 }}>
                  <MathJax>
                    <div dangerouslySetInnerHTML={{ __html: previewQuestionMarkup }} />
                  </MathJax>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      <AdminAiAssistantModal
        open={aiModalOpen}
        title={bulkAiReviewOpen ? 'Review Bulk AI Question Drafts' : 'Question AI Assistant'}
        promptLabel="What would you like the assistant to help with?"
        promptPlaceholder="Example: generate 10 unique questions for this category with varied difficulty and answer choices."
        defaultPrompt={buildBulkQuestionPrompt()}
        contextLabel="Question generation context"
        contextPlaceholder="Include the selected course, category, learning intent, difficulty level, and any topic-specific requirements."
        initialContext={`Course: ${selectedCourse?.name || 'not selected'}\nCategory: ${selectedCategory?.name || 'not selected'}\nTarget count: ${bulkAiCount}\nDifficulty: ${difficultyFilter || 'mixed'}\nSelected category id: ${categoryFilter ?? 'n/a'}`}
        applyLabel="Review question drafts"
        courseOptions={courseOptions.map((course) => ({ value: String(course.id), label: course.pathLabel || course.name }))}
        selectedCourseValue={selectedCourseId ? String(selectedCourseId) : ''}
        onCourseSelectionChange={(value) => setSelectedCourseId(value ? Number(value) : null)}
        courseSelectorLabel="Course"
        courseSelectorPlaceholder="Select a course"
        requireCourseSelection={true}
        extraControls={
          <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', fontWeight: 700, color: '#243b69' }}>
            Target question count
            <input
              type="number"
              min={1}
              max={50}
              value={bulkAiCount}
              onChange={(event) => setBulkAiCount(Math.min(50, Math.max(1, Number(event.target.value) || 1)))}
              style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
            />
          </label>
        }
        onClose={() => setAiModalOpen(false)}
        onApply={(generatedText) => handleBulkAiApply(generatedText)}
      />

      {bulkAiReviewOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12, 24, 42, 0.68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1400,
          }}
          onClick={() => setBulkAiReviewOpen(false)}
        >
          <div
            style={{
              width: 'min(1100px, 100%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
              padding: 20,
              display: 'grid',
              gap: 16,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, color: '#132744' }}>Review AI question drafts</h3>
                <div style={{ fontSize: '0.82rem', color: '#5f6f89', marginTop: 4 }}>
                  {bulkAiDrafts.length} draft questions ready to save
                </div>
              </div>
              <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setBulkAiReviewOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {bulkAiDrafts.map((draft, index) => (
                <div key={`${draft.questionText}-${index}`} style={{ border: '1px solid #dfe6f2', borderRadius: 10, padding: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontWeight: 700, color: '#142541' }}>#{index + 1} — {draft.questionText}</div>
                  <div style={{ color: '#3b4e67', display: 'grid', gap: 4 }}>
                    <div><strong>Answer:</strong> {draft.answerText}</div>
                    <div><strong>Options:</strong> A: {draft.optionA} | B: {draft.optionB} | C: {draft.optionC} | D: {draft.optionD} | E: {draft.optionE}</div>
                    <div><strong>Correct:</strong> {draft.correctOption}</div>
                    <div><strong>Difficulty:</strong> {draft.difficultyLevel}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="amd-btn amd-btn-secondary" onClick={() => setBulkAiReviewOpen(false)}>
                Cancel
              </button>
              <button type="button" className="amd-btn amd-btn-primary" onClick={() => void saveBulkGeneratedQuestions()} disabled={bulkAiSaving}>
                {bulkAiSaving ? 'Saving...' : `Save ${bulkAiDrafts.length} questions`}
              </button>
            </div>
          </div>
        </div>
      )}
    </MathJaxContext>
  );
};

export default ManageQuestions;
